/**
 * Overcut — animations éditoriales (articles magazine et landing pages).
 *
 * Quatre comportements, déclarés dans le HTML par attribut :
 *   [data-oc-reveal]          apparition au défilement (valeur : up, fade, mask, left, right)
 *   [data-oc-stagger]         décale l'apparition des enfants directs
 *   [data-oc-parallax="0.15"] léger décalage vertical d'une image pendant le défilement
 *   [data-oc-count="1200"]    compteur qui monte jusqu'au nombre à l'apparition
 *   [data-oc-progress]        barre de lecture, remplie selon la position dans [data-oc-progress-target]
 *   [data-oc-reveal-children] pose data-oc-reveal sur les descendants qui correspondent au sélecteur
 *                             donné — utile pour le corps d'un article, qui est du HTML brut
 *
 * Règle de base : sans JavaScript, ou avec « réduire les animations » activé,
 * tout le contenu est visible et immobile. Rien n'est masqué en CSS par défaut ;
 * c'est ce script qui pose la classe d'attente, et seulement sur les éléments
 * encore sous la ligne de flottaison — ceux déjà à l'écran ne clignotent pas.
 */
(() => {
  if (window.OvercutMotion) return;

  const reduit = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Apparitions ---------- */

  const observateur =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entrees) => {
            for (const entree of entrees) {
              if (!entree.isIntersecting) continue;
              montrer(entree.target);
              observateur.unobserve(entree.target);
            }
          },
          { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
        )
      : null;

  function montrer(el) {
    el.classList.add('oc-in');
    el.classList.remove('oc-wait');
    el.querySelectorAll('[data-oc-count]').forEach(compter);
    if (el.hasAttribute('data-oc-count')) compter(el);
  }

  function preparerApparition(el) {
    if (el.dataset.ocPret) return;
    el.dataset.ocPret = '1';

    if (el.hasAttribute('data-oc-stagger')) {
      [...el.children].forEach((enfant, i) => enfant.style.setProperty('--oc-i', String(i)));
    }

    const haut = el.getBoundingClientRect().top;
    if (reduit.matches || !observateur || haut < window.innerHeight * 0.92) {
      // Déjà visible au chargement : on ne cache rien après coup.
      montrer(el);
      return;
    }
    el.classList.add('oc-wait');
    observateur.observe(el);
  }

  /* ---------- Compteurs ---------- */

  function compter(el) {
    if (el.dataset.ocCompte) return;
    el.dataset.ocCompte = '1';
    const cible = parseFloat(el.dataset.ocCount);
    if (!Number.isFinite(cible)) return;
    const decimales = (el.dataset.ocCount.split('.')[1] || '').length;
    // Pas de séparateur de milliers sous 10 000 : une année (1965) doit rester une année.
    const format = (n) =>
      n.toLocaleString('fr-FR', {
        minimumFractionDigits: decimales,
        maximumFractionDigits: decimales,
        useGrouping: cible >= 10000,
      });

    if (reduit.matches) {
      el.textContent = format(cible);
      return;
    }
    const duree = 1400;
    const debut = performance.now();
    const pas = (t) => {
      const p = Math.min(1, (t - debut) / duree);
      const adouci = 1 - Math.pow(1 - p, 3);
      el.textContent = format(cible * adouci);
      if (p < 1) requestAnimationFrame(pas);
    };
    requestAnimationFrame(pas);
  }

  /* ---------- Parallaxe et barre de lecture ---------- */

  const parallaxes = new Set();
  const progressions = new Set();
  let enAttente = false;

  function surDefilement() {
    if (enAttente) return;
    enAttente = true;
    requestAnimationFrame(() => {
      enAttente = false;
      const h = window.innerHeight;

      for (const el of parallaxes) {
        if (!el.isConnected) {
          parallaxes.delete(el);
          continue;
        }
        const cadre = (el.parentElement || el).getBoundingClientRect();
        if (cadre.bottom < 0 || cadre.top > h) continue;
        const vitesse = parseFloat(el.dataset.ocParallax) || 0.15;
        // 0 quand le cadre est centré dans l'écran, ± quand il entre ou sort.
        const ecart = cadre.top + cadre.height / 2 - h / 2;
        el.style.setProperty('--oc-parallax', `${(-ecart * vitesse).toFixed(1)}px`);
      }

      for (const barre of progressions) {
        if (!barre.isConnected) {
          progressions.delete(barre);
          continue;
        }
        const cible = document.querySelector(barre.dataset.ocProgressTarget || 'main');
        if (!cible) continue;
        const r = cible.getBoundingClientRect();
        const parcours = r.height - h;
        const p = parcours > 0 ? Math.min(1, Math.max(0, -r.top / parcours)) : 1;
        barre.style.setProperty('--oc-progress', p.toFixed(4));
      }
    });
  }

  window.addEventListener('scroll', surDefilement, { passive: true });
  window.addEventListener('resize', surDefilement, { passive: true });

  /* ---------- Initialisation ---------- */

  function init(racine = document) {
    racine.querySelectorAll('[data-oc-reveal-children]').forEach((conteneur) => {
      const trouves = [...conteneur.querySelectorAll(conteneur.dataset.ocRevealChildren)];
      // Seulement les plus hauts : un bloc et son paragraphe ne s'animent pas deux fois.
      for (const el of trouves) {
        if (trouves.some((autre) => autre !== el && autre.contains(el))) continue;
        if (!el.hasAttribute('data-oc-reveal')) el.setAttribute('data-oc-reveal', 'up');
      }
    });
    racine.querySelectorAll('[data-oc-reveal], [data-oc-stagger]').forEach(preparerApparition);
    racine.querySelectorAll('[data-oc-count]').forEach((el) => {
      if (!el.closest('[data-oc-reveal], [data-oc-stagger]')) preparerApparition(el);
    });

    if (!reduit.matches) {
      racine.querySelectorAll('[data-oc-parallax]').forEach((el) => parallaxes.add(el));
    }
    racine.querySelectorAll('[data-oc-progress]').forEach((el) => progressions.add(el));
    surDefilement();
  }

  window.OvercutMotion = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Éditeur de thème : une section rechargée arrive sans ses animations.
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
